import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { useTranslations } from 'use-intl';
import type { SessionExerciseInput, SessionInput } from '@shared/schemas';
import { todayISO } from '@shared/dates';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useMicrocycles,
  usePickableExercises,
  useDeleteSession,
  useSaveSession,
  useSession,
  useSessions,
  useTeams,
  type PickableExercise,
} from '@/app/queries';
import { useAuthForm } from '@/routes/auth/useAuthForm';
import { formatDate } from '@/lib/format';
import { estimateExerciseRpe, estimateSessionRpe, sessionLoad } from '@/lib/loadClient';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/ui/Field';
import { Formula } from '@/ui/Formula';
import { EmptyState, Notice, PageHeader, Panel } from '@/ui/Panel';
import { Section } from '@/ui/Section';
import { Reorder } from '@/ui/Reorder';
import { moveItem } from '@/lib/reorder';
import { Sheet, SheetRow } from '@/ui/Sheet';
import { Chip } from '@/ui/Chip';
import { AiPlanner } from '@/routes/sessions/AiPlanner';

function blank(teamId: string, onDate: string): SessionInput {
  return { teamId, onDate, status: 'planned', exercises: [] } as SessionInput;
}

export function Sessions({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const teams = useTeams();
  const teamList = teams.data ?? [];
  const [teamId, setTeamId] = useState<string | null>(null);
  const activeTeamId = teamId ?? teamList[0]?.id ?? null;

  const today = todayISO();
  const sessions = useSessions(activeTeamId ?? undefined);
  const cycles = useMicrocycles(activeTeamId ?? undefined);
  const library = usePickableExercises();
  const save = useSaveSession();
  const remove = useDeleteSession();
  // What a session has recorded against it, once it has refused to be deleted.
  const [kept, setKept] = useState<string[] | null>(null);
  const { busy, formError, fieldError, submit } = useAuthForm();

  const [editingId, setEditingId] = useState<string | null>(null);
  const detail = useSession(editingId);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<SessionInput | null>(null);
  const [hydratedFor, setHydratedFor] = useState<string | null>(null);

  const set = <K extends keyof SessionInput>(k: K) => (value: SessionInput[K]) =>
    setForm((f) => (f ? { ...f, [k]: value } : f));

  const loaded = detail.data as Record<string, unknown> | undefined;
  if (loaded && editingId && hydratedFor !== editingId) {
    const d = loaded as never as {
      teamId: string; onDate: string; startsAt: string | null; title: string | null;
      objective: string | null; matchObjective: string | null; rationale: string | null;
      plannedDurationMin: number | null; expectedPlayers: number | null; status: string;
      microcycleId: string | null;
      exercises: Array<Record<string, unknown>>;
    };
    setForm({
      teamId: d.teamId,
      onDate: d.onDate,
      startsAt: d.startsAt ?? undefined,
      title: d.title ?? undefined,
      objective: d.objective ?? undefined,
      matchObjective: d.matchObjective ?? undefined,
      rationale: d.rationale ?? undefined,
      plannedDurationMin: d.plannedDurationMin ?? undefined,
      expectedPlayers: d.expectedPlayers ?? undefined,
      microcycleId: d.microcycleId ?? undefined,
      status: d.status as SessionInput['status'],
      exercises: d.exercises.map((e) => ({
        exerciseId: (e.exerciseId as string) ?? undefined,
        name: e.name as string,
        categoryKey: (e.categoryKey as string) ?? undefined,
        durationMin: (e.durationMin as number) ?? undefined,
        sets: (e.sets as number) ?? undefined,
        reps: (e.reps as number) ?? undefined,
        workSeconds: (e.workSeconds as number) ?? undefined,
        restSeconds: (e.restSeconds as number) ?? undefined,
        intensity: (e.intensity as number) ?? undefined,
        energySystemKey: (e.energySystemKey as string) ?? undefined,
        rationale: (e.rationale as string) ?? undefined,
        notes: (e.notes as string) ?? undefined,
      })),
    } as SessionInput);
    setHydratedFor(editingId);
  }

  /**
   * The same engine the server runs, so the estimate updates as the coach edits
   * rather than only after saving. The server recomputes on save and its answer
   * is what is stored — this is the preview, not a second implementation.
   */
  const estimate = useMemo(() => {
    if (!form) return null;
    const perExercise = form.exercises.map((e) => ({
      name: e.name,
      durationMin: e.durationMin ?? 0,
      rpe: estimateExerciseRpe({
        categoryKey: e.categoryKey,
        durationMin: e.durationMin,
        intensity: e.intensity,
        workSeconds: e.workSeconds,
        restSeconds: e.restSeconds,
        reps: e.reps,
        sets: e.sets,
        primaryEnergySystem: e.energySystemKey,
      }),
    }));
    const session = estimateSessionRpe(
      perExercise.map((e) => ({ durationMin: e.durationMin, rpe: e.rpe.value })),
    );
    const minutes =
      form.plannedDurationMin ?? perExercise.reduce((s, e) => s + e.durationMin, 0);
    return { perExercise, session, minutes, load: sessionLoad(minutes, session.value) };
  }, [form]);

  const closeForm = () => {
    setOpen(false);
    setEditingId(null);
    setKept(null);
    setHydratedFor(null);
    setForm(null);
  };

  const addExercise = (from?: PickableExercise) => {
    if (!form) return;
    const next: SessionExerciseInput = from
      ? {
          exerciseId: from.id,
          name: from.name,
          categoryKey: from.categoryKey ?? undefined,
          durationMin: from.durationMin ?? undefined,
          intensity: from.intensity ?? undefined,
          workSeconds: from.workSeconds ?? undefined,
          restSeconds: from.restSeconds ?? undefined,
          sets: from.sets ?? undefined,
          reps: from.reps ?? undefined,
        }
      : { name: '' };
    set('exercises')([...form.exercises, next]);
  };

  /**
   * Narrowing a season, not searching it.
   *
   * A club two months in has sixty sessions and wants three: the ones it has
   * not rated yet, or the ones about finishing. Both are a sentence a coach can
   * type, so the filter matches the words he would use — the title, the
   * objective, what the week was for — rather than offering him a form.
   */
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<'all' | 'planned' | 'completed'>('all');
  const [showAll, setShowAll] = useState(false);

  const all = sessions.data ?? [];
  const needle = query.trim().toLowerCase();
  const list = all.filter((s) => {
    if (status !== 'all' && s.status !== status) return false;
    if (!needle) return true;
    return [s.title, s.objective, s.matchObjective, s.onDate]
      .some((v) => (v ?? '').toLowerCase().includes(needle));
  });

  /*
    A season of sessions is a season long.

    Thirty-six rows made this one panel eight screens tall, and a coach opening
    Sessions is almost always after the next one or the last one — the list is
    sorted so both are at the top. The rest is a search away, and the button
    says how many are behind it rather than hiding the number.
  */
  const RECENT = 12;
  const shown = showAll || needle || status !== 'all' ? list : list.slice(0, RECENT);
  const hidden = list.length - shown.length;

  if (teams.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  if (teamList.length === 0) {
    return (
      <>
        <PageHeader
          title={t('sessions.title')}
          lead={t('sessions.lead')}
          help={localePath(locale, '/guide/session')}
        />
        <Panel>
          <EmptyState title={t('teams.empty')} body={t('dashboard.noTeamBody')} />
        </Panel>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t('sessions.title')}
        lead={t('sessions.lead')}
        explain="expected_load"
        help={localePath(locale, '/guide/session')}
        action={
          <Button
            variant="primary"
            onClick={() => {
              if (!activeTeamId) return;
              setEditingId(null);
              setHydratedFor(null);
              setForm(blank(activeTeamId, today));
              setOpen(true);
            }}
          >
            {t('sessions.add')}
          </Button>
        }
      />

      {teamList.length > 1 ? (
        <div className="mb-4 flex flex-wrap gap-1">
          {teamList.map((tm) => (
            <button
              key={tm.id}
              type="button"
              onClick={() => setTeamId(tm.id)}
              aria-pressed={tm.id === activeTeamId}
              className={`rounded-(--radius-control) border px-3 py-1.5 text-meta transition-colors ${
                tm.id === activeTeamId
                  ? 'border-ink bg-ink text-paper'
                  : 'border-line bg-surface text-ink-muted hover:border-ink'
              }`}
            >
              {tm.name}
            </button>
          ))}
        </div>
      ) : null}

      {open && form ? (
        <div className="mb-5">
          <Panel title={editingId ? t('sessions.edit') : t('sessions.add')}>
            <form
              noValidate
              onSubmit={async (e) => {
                e.preventDefault();
                const ok = await submit(async () => {
                  await save.mutateAsync({ sessionId: editingId ?? undefined, input: form });
                });
                if (ok) closeForm();
              }}
            >
              {formError ? (
                <div className="px-4 pt-4">
                  <Notice tone="risk">{formError}</Notice>
                </div>
              ) : null}

              {/* The draft lands in this form; nothing is saved without the coach. */}
              <Section title={t('ai.planner')} hint={t('ai.plannerHint')}>
                <AiPlanner
                  teamId={form.teamId}
                  onDate={form.onDate}
                  durationMin={form.plannedDurationMin ?? 80}
                  suggestedObjective={form.matchObjective ?? form.objective ?? null}
                  onUse={(patch) => setForm((f) => (f ? { ...f, ...patch } : f))}
                />
              </Section>

              <Section title={t('sessions.objective')}>
                <div className="grid gap-x-5 gap-y-2 sm:grid-cols-3">
                  <Field label={t('sessions.date')} error={fieldError('onDate')}>
                    {(p) => (
                      <TextInput
                        {...p}
                        type="date"
                        value={form.onDate}
                        onChange={(e) => set('onDate')(e.target.value)}
                        required
                      />
                    )}
                  </Field>
                  <Field label={t('sessions.startsAt')} optional>
                    {(p) => (
                      <TextInput
                        {...p}
                        type="time"
                        value={form.startsAt ?? ''}
                        onChange={(e) => set('startsAt')(e.target.value || undefined)}
                      />
                    )}
                  </Field>
                  <Field label={t('sessions.duration')} optional>
                    {(p) => (
                      <TextInput
                        {...p}
                        type="number"
                        min={0}
                        max={300}
                        value={form.plannedDurationMin ?? ''}
                        onChange={(e) =>
                          set('plannedDurationMin')(
                            e.target.value === '' ? undefined : Number(e.target.value),
                          )
                        }
                      />
                    )}
                  </Field>
                  <div className="sm:col-span-3">
                    <Field label={t('sessions.objective')} optional>
                      {(p) => (
                        <TextInput
                          {...p}
                          value={form.objective ?? ''}
                          onChange={(e) => set('objective')(e.target.value || undefined)}
                        />
                      )}
                    </Field>
                  </div>
                  <div className="sm:col-span-3">
                    <Field label={t('sessions.matchObjective')} optional>
                      {(p) => (
                        <TextInput
                          {...p}
                          value={form.matchObjective ?? ''}
                          onChange={(e) => set('matchObjective')(e.target.value || undefined)}
                        />
                      )}
                    </Field>
                  </div>
                  <div className="sm:col-span-3">
                    <Field
                      label={t('sessions.rationale')}
                      hint={t('sessions.rationaleHint')}
                      optional
                    >
                      {(p) => (
                        <TextArea
                          {...p}
                          value={form.rationale ?? ''}
                          onChange={(e) => set('rationale')(e.target.value || undefined)}
                        />
                      )}
                    </Field>
                  </div>
                  {(cycles.data ?? []).length > 0 ? (
                    <Field label={t('plan.title')} optional>
                      {(p) => (
                        <SelectInput
                          {...p}
                          value={form.microcycleId ?? ''}
                          onChange={(e) => set('microcycleId')(e.target.value || undefined)}
                        >
                          <option value="">{t('sessions.none')}</option>
                          {(cycles.data ?? []).map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.name}
                            </option>
                          ))}
                        </SelectInput>
                      )}
                    </Field>
                  ) : null}
                </div>
              </Section>

              <Section title={t('sessions.exercises')}>
                <div className="grid gap-3">
                  {form.exercises.map((ex, i) => (
                    <div
                      key={i}
                      className="grid gap-x-3 gap-y-2 rounded-(--radius-control) border border-line bg-surface-sunken p-3 sm:grid-cols-[1fr_5rem_5rem_5rem_5rem_auto]"
                    >
                      <TextInput
                        value={ex.name}
                        placeholder={t('sessions.exerciseName')}
                        onChange={(e) =>
                          set('exercises')(
                            form.exercises.map((x, j) =>
                              j === i ? { ...x, name: e.target.value } : x,
                            ),
                          )
                        }
                      />
                      {(
                        [
                          ['durationMin', t('sessions.exerciseDuration')],
                          ['workSeconds', t('exercises.workSeconds')],
                          ['restSeconds', t('exercises.restSeconds')],
                          ['intensity', t('exercises.intensity')],
                        ] as const
                      ).map(([field, label]) => (
                        <TextInput
                          key={field}
                          type="number"
                          aria-label={label}
                          placeholder={label}
                          value={(ex[field] as number | undefined) ?? ''}
                          onChange={(e) =>
                            set('exercises')(
                              form.exercises.map((x, j) =>
                                j === i
                                  ? {
                                      ...x,
                                      [field]:
                                        e.target.value === '' ? undefined : Number(e.target.value),
                                    }
                                  : x,
                              ),
                            )
                          }
                        />
                      ))}
                      <div className="flex items-start gap-2">
                        <span className="tabular whitespace-nowrap rounded-[3px] bg-surface px-2 py-2.5 font-condensed text-meta font-semibold text-ink-muted">
                          {estimate?.perExercise[i]?.rpe.value ?? '–'}
                        </span>
                        {/* The order here is the order of the session: the
                            server numbers them by where they sit in this list,
                            and the printed plan and the Run screen read that
                            number. A warm-up written third was a session that
                            had to be retyped to be fixed. */}
                        <Reorder
                          index={i}
                          count={form.exercises.length}
                          label={ex.name || t('sessions.exerciseName')}
                          onMove={(to) => set('exercises')(moveItem(form.exercises, i, to))}
                        />
                        <Button
                          type="button"
                          variant="quiet"
                          className="!min-h-11 !px-2"
                          onClick={() =>
                            set('exercises')(form.exercises.filter((_, j) => j !== i))
                          }
                          aria-label={t('common.removeThing', { label: ex.name || t('sessions.exerciseName') })}
                        >
                          ×
                        </Button>
                      </div>
                      <div className="sm:col-span-6">
                        <TextInput
                          value={ex.rationale ?? ''}
                          placeholder={t('sessions.exerciseRationale')}
                          className="!text-meta"
                          onChange={(e) =>
                            set('exercises')(
                              form.exercises.map((x, j) =>
                                j === i ? { ...x, rationale: e.target.value || undefined } : x,
                              ),
                            )
                          }
                        />
                      </div>
                    </div>
                  ))}

                  <div className="flex flex-wrap items-center gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      className="!text-meta"
                      onClick={() => addExercise()}
                    >
                      {t('sessions.blank')}
                    </Button>
                    <SelectInput
                      value=""
                      className="!min-h-11 w-64 !text-meta"
                      aria-label={t('sessions.fromLibrary')}
                      onChange={(e) => {
                        const found = (library.data ?? []).find((x) => x.id === e.target.value);
                        if (found) addExercise(found);
                      }}
                    >
                      <option value="">{t('sessions.fromLibrary')}</option>
                      {(library.data ?? []).map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.name}
                        </option>
                      ))}
                    </SelectInput>
                  </div>
                </div>
              </Section>

              {estimate && form.exercises.length > 0 ? (
                <Section title={t('sessions.estimate')} hint={t('sessions.estimateHint')}>
                  <p className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
                    <span className="tabular font-condensed text-metric font-semibold leading-none">
                      {estimate.load.value}
                    </span>
                    <span className="text-ink-muted">
                      {t('sessions.estimatedLoad', { value: estimate.load.value })}
                    </span>
                    <span className="tabular text-ink-muted">
                      {t('sessions.estimatedRpe', { value: estimate.session.value })}
                    </span>
                    <span className="tabular text-ink-faint">{estimate.minutes} min</span>
                  </p>
                  {/* The engine's own explanation, rather than a sentence
                      written beside it that drifts the first time either
                      changes. Both numbers answer for themselves: the load, and
                      the session RPE the load is built from. */}
                  <div className="mt-2 grid gap-x-5 gap-y-2 sm:grid-cols-2">
                    {/* Named, because stacked on a phone two identical links
                        do not say which number each one answers for. */}
                    <div>
                      <p className="text-meta text-ink-faint">{t('sessions.estimate')}</p>
                      <Formula explained={estimate.load} />
                    </div>
                    <div>
                      <p className="text-meta text-ink-faint">{t('load.term.sessionRpe')}</p>
                      <Formula explained={estimate.session} />
                    </div>
                  </div>
                </Section>
              ) : null}

              <div className="flex flex-wrap gap-2 border-t border-line px-4 py-4">
                <Button type="submit" variant="primary" busy={busy}>
                  {t('sessions.save')}
                </Button>
                <Button type="button" variant="quiet" onClick={closeForm}>
                  {t('common.cancel')}
                </Button>
                {editingId ? (
                  <Button
                    type="button"
                    variant="danger"
                    busy={remove.isPending}
                    onClick={async () => {
                      const r = await remove.mutateAsync(editingId);
                      if (r.deleted) closeForm();
                      else setKept(r.history);
                    }}
                  >
                    {t('sessions.delete')}
                  </Button>
                ) : null}
                {editingId ? (
                  <Link
                    to={localePath(locale, `/run/${editingId}`)}
                    className="ms-auto inline-flex min-h-11 items-center rounded-(--radius-control) border border-line-strong bg-surface px-4 font-medium hover:border-ink"
                  >
                    {t('sessions.run')}
                  </Link>
                ) : null}
              </div>
              {kept ? (
                <div className="mt-3">
                  <Notice tone="info">
                    {t('sessions.keptBecause', {
                      what: kept.map((h) => t(`sessions.history_${h}`)).join(' · '),
                    })}
                  </Notice>
                </div>
              ) : null}
            </form>
          </Panel>
        </div>
      ) : null}

      <Panel
        count={
          list.length === all.length
            ? all.length || undefined
            : `${list.length} / ${all.length}`
        }
        action={
          all.length > 6 ? (
            <div className="flex flex-wrap items-center gap-2">
              <TextInput
                aria-label={t('sessions.filter')}
                value={query}
                placeholder={t('sessions.filterPlaceholder')}
                onChange={(e) => setQuery(e.target.value)}
                className="!min-h-11 w-44 !text-meta"
              />
              <SelectInput
                aria-label={t('sessions.filterStatus')}
                value={status}
                onChange={(e) => setStatus(e.target.value as typeof status)}
                className="!min-h-11 w-32 !text-meta"
              >
                <option value="all">{t('sessions.statusAll')}</option>
                <option value="planned">{t('sessions.statusPlanned')}</option>
                <option value="completed">{t('sessions.statusCompleted')}</option>
              </SelectInput>
            </div>
          ) : undefined
        }
      >
        {sessions.isLoading ? (
          <p className="flex items-center gap-2 px-4 py-8 text-ink-muted">
            <Spinner /> {t('common.loading')}
          </p>
        ) : list.length === 0 ? (
          <EmptyState
            title={all.length ? t('sessions.noneMatch') : t('sessions.empty')}
            body={all.length ? t('sessions.noneMatchBody') : t('sessions.emptyBody')}
          />
        ) : (
          <Sheet>
            {shown.map((s) => (
              <SheetRow
                key={s.id}
                highlight={s.onDate === today}
                onClick={() => {
                  setEditingId(s.id);
                  setHydratedFor(null);
                  setOpen(true);
                }}
                // Pitch-side, Run was four taps through a form: open the
                // session, scroll past every field, press Run at the bottom.
                // The Guide says "one hand, three taps", and this is the screen
                // it means. Now it is one, from the list.
                action={
                  <Link
                    to={localePath(locale, `/run/${s.id}`)}
                    className="inline-flex min-h-11 items-center rounded-(--radius-control) border border-line-strong bg-surface px-3 text-meta font-medium transition-colors hover:border-ink"
                  >
                    {t('sessions.run')}
                  </Link>
                }
              >
                <span className="tabular w-24 shrink-0 text-meta text-ink-muted">
                  {formatDate(s.onDate, locale)}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">
                    {s.title || s.objective || t('sessions.title')}
                  </span>
                  <span className="tabular text-meta text-ink-faint">
                    {s.plannedLoad ? `${t('plan.planned')} ${Math.round(s.plannedLoad)} AU` : ''}
                    {s.actualLoad ? ` · ${t('plan.actual')} ${Math.round(s.actualLoad)} AU` : ''}
                  </span>
                </span>
                <Chip muted>{t(`sessions.status_${s.status}`)}</Chip>
              </SheetRow>
            ))}
          </Sheet>
        )}

        {hidden > 0 ? (
          <div className="border-t border-line p-4">
            <Button variant="secondary" onClick={() => setShowAll(true)}>
              {t('sessions.showAll', { count: hidden })}
            </Button>
          </div>
        ) : null}
      </Panel>
    </>
  );
}
